import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Check, Sparkles, Star, Mail,
  Zap, Building2, Infinity as InfinityIcon, Users, Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";
import { billingCheckout, submitWaitlistSignup, getWaitlistCount } from "@/lib/api";
import { detectBillingCurrency, PLAN_PRICING } from "@/lib/billing";
import { isSpaceCostLive, goToSpaceCost } from "@/lib/spacecost";
import DemoDialog from "@/components/DemoDialog";

const LOGO_IMAGE =
  "https://customer-assets.emergentagent.com/job_project-profit-5/artifacts/71r5twz2_Your%20paragraph%20text.png";

// FlipCost — whole-property tool. Only the one-off Single Project plan ships
// today. Pro / Unlimited subscription tiers were on the pricing page as
// "coming soon" cards but were hurting conversion (visitors saw two greyed-
// out tiers and bounced), so we now only render the single plan. When/if
// the subscriptions ship, add them back and they'll automatically appear.
const FLIPCOST_PLANS = [
  {
    id: "fc-single",
    name: "Single Project",
    price: "$18",
    cadence: "one-off",
    pitch: "Pay-as-you-go — buy as many as you need.",
    description:
      "Perfect for the homeowner planning a single renovation or flip. Pay once per project — buy more whenever you start a new deal.",
    features: [
      "Full FlipCost dashboard",
      "Room-by-room cost estimates",
      "Estimate vs Actual tracking",
      "PDF report export",
      "All currencies & regions",
      "Buy more anytime — no subscription",
    ],
    notIncluded: [
      "Sharing with collaborators",
      "Bulk-edit across multiple deals",
    ],
    cta: "Get started",
    icon: Zap,
    highlight: true,
  },
];

// SpaceCost — sister product, room-level scope. Same "single live plan" policy
// as FlipCost — Pro / Unlimited subscription tiers exist on the roadmap but
// are no longer rendered on the pricing page until they're actually live.
const SPACECOST_PLANS = [
  {
    id: "sc-single",
    name: "Single SpaceCost Project",
    plan: "single_spacecost",
    price: "R180",
    cadence: "one-off",
    pitch: "Pay-as-you-go — buy as many as you need.",
    description:
      "Plan one space end-to-end — a kitchen refit, bathroom remodel, garden build. Unlocks one project that can hold up to 3 rooms. Need more? Add 3 more rooms for only R80 from inside the project.",
    features: [
      "Full SpaceCost dashboard",
      "Up to 3 rooms in one project",
      "Add 3 more rooms anytime · R80",
      "Estimate vs Actual mode",
      "Editable cost library",
      "Multi-currency rates",
      "PDF-friendly room report",
    ],
    notIncluded: [],
    cta: "Buy now",
    icon: Zap,
    highlight: true,
    available: true,
  },
];

const FAQ = [
  {
    q: "How does pricing work?",
    a: "Both tools are pay-as-you-go, no subscription. A SpaceCost project unlock costs ZAR 180 ($12) and gives you one room-level project with up to 3 rooms (need more rooms? add a +3 Room Pack for ZAR 80 from inside the project). A FlipCost project unlock costs ZAR 280 ($18) and gives you one full-house renovation project with no room cap. Buy as many of either as you need.",
  },
  {
    q: "Can I try it before paying?",
    a: "Yes — there's a fully-interactive live demo on the FlipCost and SpaceCost home pages (no sign-up required). It uses a pre-filled sample project so you can click through every tab. New signups also get one free 7-day FlipCost trial (1-room cap) without paying.",
  },
  {
    q: "What's the difference between FlipCost and SpaceCost?",
    a: "Use SpaceCost when you're redoing one specific space — a kitchen, a bathroom, a single room. Use FlipCost when you're doing a whole-property renovation or a flip — multiple rooms, exterior work, systems, and you want profit/ROI calcs. Most homeowners and designers start with SpaceCost.",
  },
  {
    q: "Which payment methods do you accept?",
    a: "All major credit and debit cards (Visa, Mastercard, Amex), Apple Pay and Google Pay. Billing is processed securely by Paystack.",
  },
  {
    q: "Will my data be saved if I buy?",
    a: "Yes. Anything you build in a paid project is yours — edit it forever, export PDF reports anytime, share it with collaborators. The only thing that's time-limited is the free trial; once you buy the project becomes permanent.",
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notified, setNotified] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const currency = detectBillingCurrency();
  const pricing = PLAN_PRICING[currency];

  // Fetch the live waitlist counter on mount (silent on error).
  useEffect(() => {
    let cancelled = false;
    getWaitlistCount()
      .then((res) => { if (!cancelled) setWaitlistCount(res?.count ?? null); })
      .catch(() => { /* silently degrade — counter just won't appear */ });
    return () => { cancelled = true; };
  }, []);

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    const email = notifyEmail.trim();
    if (!email) return;
    setNotifyLoading(true);
    try {
      await submitWaitlistSignup({ email });
      setNotified(true);
      // Refresh counter so the user sees their own signup reflected.
      try {
        const res = await getWaitlistCount();
        setWaitlistCount(res?.count ?? null);
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save your email — try again.");
    } finally {
      setNotifyLoading(false);
    }
  };

  // Replace the hard-coded $ prices in FLIPCOST_PLANS with the locale-aware
  // ones (USD or ZAR) so South-African visitors see R-values and overseas
  // users see USD.
  const flipcostPlans = FLIPCOST_PLANS.map((p) => {
    const key = p.id.replace("fc-", "");
    const pr = pricing[key];
    if (!pr) return p;
    return { ...p, price: pr.label, cadence: pr.cadence };
  });
  // Apply locale-aware ZAR labels to SpaceCost plans (single + room_pack
  // ship in ZAR today). Pro/Unlimited stay as `—` — they're waitlist-only.
  // We also interpolate the room_pack price into the SpaceCost Single
  // copy so USD visitors see "+3 rooms for $5" instead of the hardcoded R80.
  const roomPackLabel = pricing?.room_pack?.label || "R80";
  const spacecostPlans = SPACECOST_PLANS.map((p) => {
    const pr = p.plan ? pricing?.[p.plan] : null;
    const withRoomLabel = (s) => (typeof s === "string" ? s.replaceAll("R80", roomPackLabel) : s);
    const next = {
      ...p,
      description: withRoomLabel(p.description),
      features: (p.features || []).map(withRoomLabel),
    };
    if (!pr) return next;
    return { ...next, price: pr.label, cadence: pr.cadence };
  });

  const startCheckout = async (plan) => {
    if (!user) {
      navigate(`/auth?redirect=${encodeURIComponent(`/pricing?plan=${plan}`)}`);
      return;
    }
    setCheckoutLoading(plan);
    try {
      const callback_url = `${window.location.origin}/billing/callback`;
      const { authorization_url } = await billingCheckout({ plan, currency, callback_url });
      window.location.href = authorization_url;
    } catch (err) {
      const code = err?.response?.status;
      if (code === 503) {
        toast.error("Paystack isn't live yet — drop your email below and we'll notify you.");
        document.getElementById("notify")?.scrollIntoView({ behavior: "smooth" });
      } else {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not start checkout");
      }
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-[#1C1C1C] fc-fade-in" data-testid="pricing-page">
      {/* ---------- Top Nav ---------- */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-black/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3.5 flex items-center justify-between">
          <button
            data-testid="pricing-logo-btn"
            onClick={() => navigate("/")}
            className="flex items-baseline gap-2.5 sm:gap-3 text-[#1C1C1C] group shrink-0 min-w-0"
            aria-label="MyPlanCost home"
          >
            <span className="fc-heading text-2xl sm:text-[28px] font-extrabold tracking-tight text-[#1C1C1C] leading-none">
              MyPlanCost
            </span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
              FlipCost &amp; SpaceCost
            </span>
          </button>
          <nav className="flex items-center gap-2 sm:gap-5 text-sm">
            <button
              data-testid="pricing-nav-back"
              onClick={() => navigate("/")}
              className="hidden sm:inline-flex items-center gap-1.5 text-[#78716C] hover:text-[#1C1C1C] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Home
            </button>
            <button
              data-testid="pricing-nav-faq"
              onClick={() => navigate("/faq")}
              className="text-[#78716C] hover:text-[#1C1C1C] transition-colors"
            >
              FAQ
            </button>
            <Button
              data-testid="pricing-nav-app"
              onClick={() => navigate("/projects")}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] h-9 px-4 rounded-lg text-sm"
            >
              Open app
            </Button>
          </nav>
        </div>
      </header>

      {/* ---------- Hero ---------- */}
      <section className="relative pt-16 sm:pt-24 pb-10" data-testid="pricing-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-sage-700 bg-sage-100 border border-sage-200 rounded-full px-3 py-1">
            <Sparkles className="w-3 h-3" /> Pricing
          </div>
          <h1 className="fc-heading text-4xl sm:text-6xl font-medium tracking-tight mt-6 leading-[1.05]">
            Pay for what you <span className="text-sage-700">actually need.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#57534E] mt-6 max-w-2xl mx-auto leading-relaxed">
            Built for clarity, priced for simplicity. <strong>FlipCost</strong> and <strong>SpaceCost</strong> give you the tools to plan properly — without hidden costs or complicated tiers.
          </p>
          <p className="text-[13px] text-[#78716C] mt-4 max-w-2xl mx-auto leading-relaxed">
            Try the live demo by clicking below — no sign-up or card details required.
          </p>
          <div className="mt-8">
            <Button
              data-testid="pricing-hero-demo-btn"
              onClick={() => setDemoOpen(true)}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-11 px-5 text-sm font-medium inline-flex items-center gap-2"
            >
              <Play className="w-4 h-4" /> Try the Live Demo — no signup
            </Button>
            <div className="text-[11px] text-[#A8A29E] mt-2">See FlipCost in action before you pay.</div>
          </div>
        </div>
      </section>

      {/* ---------- SpaceCost Plans (FIRST — single-room jobs are more common than full flips) ---------- */}
      <section className="py-12 sm:py-16 bg-sage-50/40 border-y border-sage-200/50" data-testid="pricing-spacecost-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <ToolHeader
            eyebrow="SpaceCost"
            badge="Live"
            badgeTone="live"
            title="Single-room & single-space budgets."
            subtitle="Priced for a single space, not a whole home — buy as many SpaceCost projects as you need, no subscription."
          />
          <div className="text-center mt-5">
            <button
              data-testid="pricing-spacecost-open-btn"
              onClick={() => navigate("/spacecost")}
              className="inline-flex items-center gap-2 text-sage-700 hover:text-sage-800 bg-white hover:bg-sage-50 border border-sage-300 rounded-full h-9 px-4 text-[13px] font-medium transition-colors"
            >
              <ArrowRight className="w-3.5 h-3.5" /> Open SpaceCost
            </button>
            <div className="text-[11px] text-[#A8A29E] mt-1.5">
              SpaceCost shares the same UX — scoped to one space, with a +3 Room Pack add-on for projects that need more rooms.
            </div>
          </div>
          <div className="mt-10 grid grid-cols-1 max-w-md mx-auto">
            {spacecostPlans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                lightHighlight
                loading={checkoutLoading === p.plan}
                onSelect={() => startCheckout(p.plan)}
              />
            ))}
          </div>
          <p className="text-center text-[12px] text-[#A8A29E] mt-8 max-w-2xl mx-auto">
            Prices shown in {currency}. SpaceCost projects are once-off purchases — no subscription required.
          </p>
        </div>
      </section>

      {/* ---------- FlipCost Plans ---------- */}
      <section className="py-12 sm:py-16" data-testid="pricing-flipcost-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <ToolHeader
            eyebrow="FlipCost"
            badge="Available now"
            badgeTone="live"
            title="Whole-property renovations & flips."
            subtitle="For flippers and homeowners doing a full-home renovation or flip."
          />
          <div className="text-center mt-5">
            <button
              data-testid="pricing-flipcost-demo-btn"
              onClick={() => setDemoOpen(true)}
              className="inline-flex items-center gap-2 text-sage-700 hover:text-sage-800 bg-sage-100 hover:bg-sage-200 border border-sage-200 rounded-full h-9 px-4 text-[13px] font-medium transition-colors"
            >
              <Play className="w-3.5 h-3.5" /> Try the Live Demo
            </button>
          </div>
          <div className="mt-10 grid grid-cols-1 max-w-md mx-auto">
            {flipcostPlans.map((p) => {
              const planKey = p.id.replace("fc-", "");
              return (
                <PlanCard
                  key={p.id}
                  plan={p}
                  lightHighlight
                  loading={checkoutLoading === planKey}
                  onSelect={() => startCheckout(planKey)}
                />
              );
            })}
          </div>
          <p className="text-center text-[12px] text-[#A8A29E] mt-8 max-w-2xl mx-auto">
            Prices shown in {currency}. FlipCost is a once-off project purchase — buy as many as you need, no subscription.
          </p>
        </div>
      </section>

      {/* ---------- Compare strip ---------- */}
      <section className="py-12 bg-white border-y border-black/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="fc-label">How to pick</div>
            <h3 className="fc-heading text-2xl sm:text-3xl font-medium tracking-tight mt-2">FlipCost or SpaceCost?</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <div className="fc-card p-6">
              <div className="fc-heading text-sage-700 text-[11px] uppercase tracking-[0.18em]">FlipCost</div>
              <div className="fc-heading text-[15px] font-medium text-[#1C1C1C] mt-1.5">Renovating or flipping an entire property</div>
              <p className="text-[13px] text-[#57534E] mt-2 leading-relaxed">
                Whole-home scope — rooms, exterior, systems, extensions, and soft costs. Pick this if you're planning a full renovation, deciding on a flip, or tracking a whole-property build end-to-end.
              </p>
            </div>
            <div className="fc-card p-6">
              <div className="fc-heading text-sage-700 text-[11px] uppercase tracking-[0.18em]">SpaceCost</div>
              <div className="fc-heading text-[15px] font-medium text-[#1C1C1C] mt-1.5">Planning a single room or area</div>
              <p className="text-[13px] text-[#57534E] mt-2 leading-relaxed">
                Single-space scope — kitchen refit, bathroom remodel, garden build, single extension. Pick this if you're focused on one room or area and don't need the whole-property model.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="py-16 sm:py-20" data-testid="pricing-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="text-center">
            <div className="fc-label">Pricing FAQs</div>
            <h2 className="fc-heading text-3xl sm:text-4xl font-medium tracking-tight mt-3">
              Common questions.
            </h2>
          </div>
          <div className="mt-10 divide-y divide-black/[0.06] border border-black/[0.06] rounded-xl bg-white">
            {FAQ.map((f) => (
              <FaqRow key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Notify CTA ---------- */}
      <section
        id="notify"
        data-testid="pricing-notify"
        className="py-16 sm:py-20 bg-[#0F0E0C] text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-[0.18] pointer-events-none" aria-hidden="true" style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(168,186,153,0.5) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(168,186,153,0.4) 0%, transparent 50%)",
        }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="fc-heading text-3xl sm:text-5xl font-medium tracking-tight leading-[1.05]">
            Stay in the <span className="text-sage-300">loop.</span>
          </h2>
          <p className="text-white/70 mt-4 max-w-xl mx-auto text-base leading-relaxed">
            New regions, new tools, new features — get a heads-up email when something useful ships. No spam, unsubscribe anytime.
          </p>
          {waitlistCount !== null && (
            <div
              data-testid="pricing-waitlist-counter"
              className="mt-6 inline-flex items-center gap-2 text-[12px] text-sage-300 bg-sage-300/10 border border-sage-300/30 rounded-full px-3.5 py-1.5"
            >
              <span className="relative inline-flex w-2 h-2">
                <span className="absolute inline-flex w-2 h-2 rounded-full bg-sage-300 animate-ping opacity-60" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-sage-300" />
              </span>
              <Users className="w-3.5 h-3.5" />
              <span>
                <span data-testid="pricing-waitlist-count" className="font-medium">{waitlistCount.toLocaleString()}</span>{" "}
                people on the waitlist · join them
              </span>
            </div>
          )}
          {notified ? (
            <div className="mt-8 inline-flex items-center gap-2 text-sm text-sage-300 bg-sage-300/10 border border-sage-300/30 rounded-full px-4 py-2">
              <Check className="w-4 h-4" /> Got it — we'll be in touch.
            </div>
          ) : (
            <form
              onSubmit={handleNotifySubmit}
              className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                data-testid="pricing-notify-email"
                type="email"
                required
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 h-11 px-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 outline-none focus:border-sage-300/60 focus:bg-white/15 transition-colors"
              />
              <Button
                data-testid="pricing-notify-submit"
                type="submit"
                disabled={notifyLoading}
                className="bg-sage-300 text-[#1A1917] hover:bg-sage-200 h-11 px-5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5"
              >
                {notifyLoading ? "Saving…" : "Notify me"} <Mail className="w-4 h-4" />
              </Button>
            </form>
          )}
          <p className="text-[11px] text-white/40 mt-4">
            We'll only use your email to let you know about pricing — no spam.
          </p>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="bg-[#1C1C1C] text-white/70" data-testid="pricing-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 flex flex-wrap items-center justify-between gap-4 text-[12px]">
          <div className="inline-flex items-center gap-2">
            <img src={LOGO_IMAGE} alt="MyPlanCost" className="h-9 w-auto" />
            <span className="fc-heading text-white text-sm">MyPlanCost</span>
          </div>
          <div className="flex items-center gap-5">
            <button onClick={() => navigate("/")} className="hover:text-white">Home</button>
            <button onClick={() => navigate("/flipcost")} className="hover:text-white">FlipCost</button>
            <button onClick={() => navigate("/faq")} className="hover:text-white">FAQ</button>
            <button onClick={() => navigate("/disclaimer")} className="hover:text-white">Disclaimer</button>
          </div>
          <div>© {new Date().getFullYear()} MyPlanCost</div>
        </div>
      </footer>

      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}

function ToolHeader({ eyebrow, badge, badgeTone, title, subtitle }) {
  const badgeClass =
    badgeTone === "soon"
      ? "bg-white text-sage-700 border-sage-300"
      : "bg-sage-100 text-sage-700 border-sage-200";
  return (
    <div className="text-center max-w-2xl mx-auto">
      <div className="inline-flex items-center gap-2 flex-wrap justify-center">
        <div className="fc-label text-sage-700 text-[12px] tracking-[0.22em]">{eyebrow}</div>
        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${badgeClass}`}>
          {badge}
        </span>
      </div>
      <h2 className="fc-heading text-3xl sm:text-5xl font-medium tracking-tight mt-4 leading-[1.05]">
        {title}
      </h2>
      <p className="text-base text-[#57534E] mt-4 leading-relaxed">{subtitle}</p>
    </div>
  );
}

function PlanCard({ plan, onSelect, comingSoon = false, loading = false, darkComingSoon = false, hidePrice = false, lightHighlight = false }) {
  const { id, name, price, cadence, sub, pitch, description, features, notIncluded, cta, icon: Icon, highlight } = plan;
  // Dark treatment applies to: highlighted live cards (unless explicitly light) OR explicitly dark-coming-soon cards.
  const isDark = (highlight && !comingSoon && !lightHighlight) || (comingSoon && darkComingSoon);
  // Light-but-elevated highlight (cream card with sage border + lift).
  const isLightHero = highlight && !comingSoon && lightHighlight;
  return (
    <div
      data-testid={`plan-${id}`}
      className={`relative flex flex-col rounded-2xl p-7 sm:p-8 transition-all duration-300 ${
        isDark
          ? `bg-[#1C1C1C] text-white border-2 ${highlight ? "border-sage-300 shadow-[0_30px_60px_-25px_rgba(168,186,153,0.5)] lg:-translate-y-3" : "border-white/10"}`
          : isLightHero
            ? "bg-white text-[#1C1C1C] border-2 border-sage-300 shadow-[0_30px_60px_-25px_rgba(168,186,153,0.45)] lg:-translate-y-3"
            : highlight
              ? (comingSoon
                  ? "bg-white text-[#1C1C1C] border-2 border-sage-300 shadow-[0_20px_40px_-20px_rgba(168,186,153,0.4)] lg:-translate-y-3"
                  : "bg-[#1C1C1C] text-white border-2 border-sage-300 shadow-[0_30px_60px_-25px_rgba(168,186,153,0.5)] lg:-translate-y-3")
              : "bg-white border border-black/[0.08] hover:border-black/20 hover:-translate-y-0.5"
      } ${comingSoon ? "opacity-95" : ""}`}
    >
      {highlight && !comingSoon && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full bg-sage-300 text-[#1A1917] font-medium">
            <Star className="w-3 h-3" /> Most popular
          </span>
        </div>
      )}
      {comingSoon && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full bg-sage-300 text-[#1A1917] font-medium">
            <Sparkles className="w-3 h-3" /> Coming soon
          </span>
        </div>
      )}

      {/* Icon / name */}
      <div className={`inline-flex items-center gap-2 ${isDark ? "text-sage-300" : "text-sage-700"}`}>
        <div className={`w-9 h-9 rounded-lg grid place-items-center ${
          isDark
            ? "bg-sage-300/15 border border-sage-300/30"
            : "bg-sage-100 border border-sage-200"
        }`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-medium">{name}</div>
      </div>

      {hidePrice ? (
        <div className="mt-5">
          <div className={`fc-heading text-2xl tracking-tight font-medium ${isDark ? "text-white/80" : "text-[#1C1C1C]"}`}>
            Pricing TBD
          </div>
          <div className={`text-[12px] mt-1 ${isDark ? "text-sage-300" : "text-sage-700"}`}>
            Notify me when it launches
          </div>
        </div>
      ) : (
        <>
          <div className="mt-5 flex items-baseline gap-1.5">
            <div className={`fc-heading text-5xl tracking-tight font-medium ${isDark ? "text-white" : "text-[#1C1C1C]"}`}>
              {price}
            </div>
            <div className={`text-sm ${isDark ? "text-white/60" : "text-[#78716C]"}`}>{cadence}</div>
          </div>
          {sub && (
            <div className={`text-[12px] mt-1 ${isDark ? "text-sage-300" : "text-sage-700"}`}>
              {sub}
            </div>
          )}
        </>
      )}

      <p className={`text-[13px] mt-4 leading-relaxed ${isDark ? "text-white/80" : "text-[#57534E]"}`}>
        {description}
      </p>

      <ul className="mt-6 space-y-2.5 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-[13px]">
            <Check className={`w-3.5 h-3.5 mt-[3px] shrink-0 ${isDark ? "text-sage-300" : "text-sage-700"}`} />
            <span className={isDark ? "text-white/90" : "text-[#27272A]"}>{f}</span>
          </li>
        ))}
        {notIncluded.map((f) => (
          <li key={f} className="flex items-start gap-2 text-[13px] opacity-50">
            <span className={`w-3.5 h-3.5 mt-[3px] shrink-0 inline-flex items-center justify-center text-[10px] ${isDark ? "text-white/50" : "text-[#A8A29E]"}`}>—</span>
            <span className={isDark ? "text-white/60 line-through" : "text-[#78716C] line-through"}>{f}</span>
          </li>
        ))}
      </ul>

      <Button
        data-testid={`plan-${id}-cta`}
        onClick={onSelect}
        disabled={loading}
        className={`mt-8 h-11 px-5 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5 ${
          isDark
            ? "bg-sage-300 text-[#1A1917] hover:bg-sage-200"
            : "bg-[#1C1C1C] text-white hover:bg-[#2A2A2E]"
        }`}
      >
        {loading ? "Redirecting…" : (comingSoon ? "Notify me" : cta)} <ArrowRight className="w-4 h-4" />
      </Button>
      <div className={`text-[10px] uppercase tracking-[0.18em] text-center mt-3 ${isDark ? "text-white/40" : "text-[#A8A29E]"}`}>
        {pitch}
      </div>

      {/* Coming-soon "veil" — covers the description + feature list so the
          plan name + price stub up top stay visible (visitors know which
          tier is unavailable), but the feature details are hidden behind a
          frosted-glass layer with a clear COMING SOON banner across the
          middle. The CTA + pitch line at the bottom stay outside the veil
          so the "Notify me" button is always clickable and obviously
          interactive. */}
      {comingSoon && (
        <div
          className="absolute left-0 right-0 top-[140px] bottom-[110px] rounded-md pointer-events-none overflow-hidden"
          aria-hidden="true"
          data-testid={`plan-${id}-coming-soon-veil`}
        >
          <div
            className={`absolute inset-0 ${
              isDark
                ? "bg-[#1C1C1C]/10 backdrop-blur-[2px]"
                : "bg-white/10 backdrop-blur-[2px]"
            }`}
          />
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center">
            <div
              className={`w-[120%] -mx-[10%] text-center py-3 ${
                isDark
                  ? "bg-sage-300 text-[#1A1917]"
                  : "bg-[#1C1C1C] text-white"
              } -rotate-2`}
              style={{ boxShadow: "0 18px 40px -16px rgba(0,0,0,0.35)" }}
            >
              <div className="fc-heading text-[15px] sm:text-[17px] tracking-[0.18em] uppercase font-bold">
                Coming soon
              </div>
              <div className={`text-[10px] mt-0.5 tracking-[0.12em] uppercase ${isDark ? "text-[#1A1917]/70" : "text-white/65"}`}>
                Notify me to unlock
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      className="w-full text-left px-5 sm:px-6 py-4 flex items-start gap-4 hover:bg-sage-50/40 transition-colors"
    >
      <div className="flex-1">
        <div className="fc-heading text-[14px] text-[#1C1C1C] font-medium">{q}</div>
        {open && <p className="text-[13px] text-[#57534E] mt-2 leading-relaxed">{a}</p>}
      </div>
      <span className={`text-[#A8A29E] text-base shrink-0 transition-transform ${open ? "rotate-45" : ""}`}>+</span>
    </button>
  );
}
